/**
 * Cliente da API.
 *
 * Fino de propósito. Toda rota vive sob `/projetos/:projetoId/...`, então o
 * projeto não é parâmetro de cada chamada: é estado do cliente. Isso espelha
 * o servidor, onde o escopo é topológico — e evita a classe de bug em que uma
 * tela esquece de passar o projeto e lê dado da escola errada.
 */

/**
 * Mesma origem, por padrão.
 *
 * `/api` é servido pelo proxy do Vite em desenvolvimento e por um proxy
 * reverso em produção. Isso é o que permite ao cookie de sessão ser
 * `SameSite=Lax` — ver o comentário em `vite.config.ts`.
 */
const BASE = import.meta.env.VITE_API ?? '/api'

/**
 * Onde o fluxo de eventos vive.
 *
 * `EventSource` não aceita cabeçalhos, então ele depende do cookie — que só
 * viaja se a origem for a mesma. É o mesmo motivo do proxy, visto de outro
 * ângulo.
 */
export const BASE_EVENTOS = BASE

export type Linha = Record<string, unknown>

export type Grupo = 'ESTRUTURA' | 'ACADEMICO' | 'AGENDAVEL'

/**
 * O manual de um destino, em três partes.
 *
 * Vem do catálogo da API junto com o resto da definição do recurso, pelo mesmo
 * motivo que a navegação vem de lá: lista mantida em dois lugares diverge.
 */
/**
 * Um passo do tour, dito em termos de domínio.
 *
 * `campo` é o nome da coluna e `marca` é um rótulo que a tela declara em
 * `data-tour`. A tradução para seletor de CSS acontece em `Tour.tsx`, e em
 * nenhum outro lugar — o catálogo da API não sabe o que é uma classe.
 */
export interface PassoDoTour {
  campo?: string
  marca?: string
  texto: string
}

export interface Ajuda {
  /** Uma frase: para que serve. */
  paraQue: string
  /** Os passos, na ordem em que a pessoa os faz. */
  comoUsar: string[]
  /** O que costuma dar errado aqui, e o que fazer. */
  cuidados?: string[]
  /** O mesmo caminho, apontado na tela aberta. Opcional: ver o catálogo. */
  tour?: PassoDoTour[]
}

/**
 * Uma relação entre duas entidades, como a API a descreve.
 *
 * Vem de `/relacoes`, cujo comentário no servidor diz, desde o início, "para
 * a interface montar os campos sozinha". Até 27/10 a interface não montava, e
 * co-docência, locais permitidos, turmas de uma trilha e mais cinco só
 * existiam para quem chamava a API à mão.
 */
export interface Relacao {
  /** Molde da rota: `turmas/:id/esquemas`. */
  caminho: string
  entidade: string
  /** Tabela pai — `turma`, `oferta`. */
  pai: string
  referencias: { coluna: string; aponta_para: string; rotulo: string }[]
  /** Dados próprios da relação, que não apontam para ninguém. */
  campos: string[]
}

export interface Recurso {
  caminho: string
  /** Nome da tabela. É por ele que as relações se ligam ao recurso. */
  tabela: string
  rotulo: string
  grupo: Grupo
  entidade: string
  campos: string[]
  ajuda: Ajuda
  /** Aparece no menu deste projeto. Ausente na listagem sem projeto. */
  ativo?: boolean
}

export class ErroApi extends Error {
  readonly status: number
  constructor(status: number, mensagem: string) {
    super(mensagem)
    this.status = status
    this.name = 'ErroApi'
  }
}

/**
 * A identidade vem do cookie de sessão, e o navegador o envia sozinho.
 *
 * Antes daqui saía um cabeçalho `x-pauta-usuario` com o que estivesse no
 * `localStorage` — qualquer um era quem quisesse ser. O cookie é `httpOnly`,
 * então **este código não consegue lê-lo**, e é essa a intenção: um XSS na
 * página não tem como roubar a sessão.
 */
export async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    ...init,
    // Manda o cookie mesmo quando alguém apontar a interface direto na porta
    // da API, fora do proxy.
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!resposta.ok) {
    const corpo = await resposta.json().catch(() => ({}))
    throw new ErroApi(resposta.status, (corpo as { erro?: string }).erro ?? resposta.statusText)
  }
  return resposta.status === 204 ? (undefined as T) : ((await resposta.json()) as T)
}

export interface Usuario {
  id: string
  email: string
  nome: string
}

export interface ProjetoDoUsuario {
  id: string
  nome: string
  ano_letivo: number
  papel: 'PROPRIETARIO' | 'EDITOR' | 'LEITOR'
}

/**
 * A escola inteira — o que `ProjetoDoUsuario` não traz.
 *
 * A lista da troca de escola viaja a cada carregamento e por isso carrega só
 * o indispensável. Estes campos vêm num pedido por vez, na tela que os usa.
 */
export interface Escola extends ProjetoDoUsuario {
  semanas_no_mes: string
  grau_fora_do_turno: string
  /** Quantos horários colados formam um quadradinho do formulário (ESPM: 2, o tempo de 1h40). */
  creditos_por_tempo: number
  /** Prazo do link, em dias, ao gerar ou reabrir. */
  dias_do_formulario: number
  /** Texto do convite com {link}, {prazo}, {periodo}; nulo usa o padrão. */
  convite_formulario: string | null
  /** Quem recebe o alerta de formulário sem resposta ou sem cobrir. */
  emails_coordenacao: string[]
  criado_em: string
  atualizado_em: string
}

export interface Membro {
  id: string
  usuario_id: string
  email: string
  papel: 'PROPRIETARIO' | 'EDITOR' | 'LEITOR'
  criado_em: string
}

export interface Convite {
  id: string
  email: string
  papel: string
  criado_em: string
  expira_em: string
  usado_em: string | null
}

/**
 * Gerenciar a escola: o que ela é, e quem entra nela.
 *
 * **O convite devolve o token UMA vez.** O servidor guarda só o hash, como
 * faz com sessão, então a tela precisa mostrar o link no mesmo instante em
 * que o cria — quem fechar sem copiar emite outro. É mais trabalho de
 * interface do que guardar o token, e é a escolha certa: token guardado é
 * token que vaza.
 */
export const escola = {
  ler: (projeto: string) => pedir<Escola>(`/projetos/${projeto}`),

  alterar: (projeto: string, mudanca: Partial<Record<string, unknown>>) =>
    pedir<Escola>(`/projetos/${projeto}`, { method: 'PATCH', body: JSON.stringify(mudanca) }),

  excluir: (projeto: string) => pedir<void>(`/projetos/${projeto}`, { method: 'DELETE' }),

  membros: (projeto: string) =>
    pedir<{ itens: Membro[] }>(`/projetos/${projeto}/membros`).then((r) => r.itens),

  adicionarMembro: (projeto: string, email: string, papel: string) =>
    pedir<Membro>(`/projetos/${projeto}/membros`, {
      method: 'POST',
      body: JSON.stringify({ email, papel }),
    }),

  removerMembro: (projeto: string, id: string) =>
    pedir<void>(`/projetos/${projeto}/membros/${id}`, { method: 'DELETE' }),

  convites: (projeto: string) =>
    pedir<{ itens: Convite[] }>(`/projetos/${projeto}/convites`).then((r) => r.itens),

  convidar: (projeto: string, email: string, papel: string) =>
    pedir<Convite & { token: string }>(`/projetos/${projeto}/convites`, {
      method: 'POST',
      body: JSON.stringify({ email, papel }),
    }),

  revogarConvite: (projeto: string, id: string) =>
    pedir<void>(`/projetos/${projeto}/convites/${id}`, { method: 'DELETE' }),
}

export const sessao = {
  eu: () => pedir<{ usuario: Usuario; projetos: ProjetoDoUsuario[] }>('/auth/eu'),

  entrar: (email: string, senha: string) =>
    pedir<Usuario>('/auth/entrar', { method: 'POST', body: JSON.stringify({ email, senha }) }),

  registrar: (email: string, senha: string, nome: string) =>
    pedir<Usuario>('/auth/registrar', {
      method: 'POST',
      body: JSON.stringify({ email, senha, nome }),
    }),

  sair: () => pedir<void>('/auth/sair', { method: 'POST' }),
}

export const api = {
  /** O catálogo inteiro, sem projeto — é por ele que se oferece o que religar. */
  recursos: () => pedir<{ itens: Recurso[] }>('/recursos').then((r) => r.itens),

  /** O catálogo deste projeto, com o que está ativo. A navegação sai daqui. */
  recursosDoProjeto: (projeto: string) =>
    pedir<{ itens: Recurso[] }>(`/projetos/${projeto}/recursos`).then((r) => r.itens),

  /** Redefine o que aparece no menu. Só navegação: não toca em dado nenhum. */
  definirMenu: (projeto: string, ativos: string[]) =>
    pedir<{ itens: Recurso[] }>(`/projetos/${projeto}/recursos`, {
      method: 'PUT',
      body: JSON.stringify({ ativos }),
    }).then((r) => r.itens),

  listar: (projeto: string, caminho: string) =>
    pedir<{ itens: Linha[] }>(`/projetos/${projeto}/${caminho}`).then((r) => r.itens),

  criar: (projeto: string, caminho: string, dados: Linha) =>
    pedir<Linha>(`/projetos/${projeto}/${caminho}`, {
      method: 'POST',
      body: JSON.stringify(dados),
    }),

  atualizar: (projeto: string, caminho: string, id: string, dados: Linha) =>
    pedir<Linha>(`/projetos/${projeto}/${caminho}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    }),

  excluir: (projeto: string, caminho: string, id: string) =>
    pedir<void>(`/projetos/${projeto}/${caminho}/${id}`, { method: 'DELETE' }),

  /** O catálogo das relações. Uma vez por sessão basta: é código, não dado. */
  relacoes: () => pedir<{ itens: Relacao[] }>('/relacoes').then((r) => r.itens),

  listarRelacao: (projeto: string, caminho: string) =>
    pedir<{ itens: Linha[] }>(`/projetos/${projeto}/${caminho}`).then((r) => r.itens),

  /**
   * Substitui a relação inteira.
   *
   * O corpo descreve o estado final, e não um delta — mesma decisão do menu e
   * da atribuição. Delta exige que os dois lados concordem sobre o que havia
   * antes, e eles não concordam quando duas abas estão abertas.
   */
  substituirRelacao: (projeto: string, caminho: string, itens: Linha[]) =>
    pedir<{ itens: Linha[] }>(`/projetos/${projeto}/${caminho}`, {
      method: 'PUT',
      body: JSON.stringify({ itens }),
    }).then((r) => r.itens),

  slots: (projeto: string, esquema: string) =>
    pedir<{ itens: Linha[] }>(`/projetos/${projeto}/esquemas/${esquema}/slots`).then(
      (r) => r.itens,
    ),

  /**
   * Gera os horários de um esquema a partir de uma receita.
   *
   * "Oito tempos de 50 minutos a partir das 7h30, de segunda a sexta, com
   * recreio de 20 depois do terceiro" — e não trinta cadastros idênticos à
   * mão, que é o tipo de trabalho que faz a pessoa desistir antes de chegar
   * na grade.
   */
  gerarSlots: (
    projeto: string,
    esquema: string,
    receita: {
      dias: number[]
      inicio: string
      duracao_min: number
      aulas: number
      intervalo_apos?: number | null
      intervalo_min?: number
    },
  ) =>
    pedir<{ itens: Linha[] }>(`/projetos/${projeto}/esquemas/${esquema}/slots`, {
      method: 'PUT',
      body: JSON.stringify(receita),
    }).then((r) => r.itens),

  apagarSlots: (projeto: string, esquema: string) =>
    pedir<{ itens: Linha[] }>(`/projetos/${projeto}/esquemas/${esquema}/slots`, {
      method: 'PUT',
      body: JSON.stringify({ itens: [] }),
    }).then((r) => r.itens),
}

// ---------------------------------------------------------------------------
// A grade
// ---------------------------------------------------------------------------

export interface SlotGrade {
  id: string
  esquema_id: string
  dia: number
  /** Já em `HH:MM` — a conversão de minuto do dia acontece no servidor. */
  inicio: string
  duracao_min: number
  tipo: 'AULA' | 'INTERVALO' | 'ALMOCO' | 'JANTA'
  ordem: number
  esquema: string
  turno_id: string
  turno: string
}

export interface AlocacaoGrade {
  id: string
  tipo: 'AULA' | 'HORA_ATIVIDADE'
  slot_id: string
  esquema_id: string
  fixada: boolean
  /** Posta à mão fora da disponibilidade, de propósito, enquanto se negocia. */
  na_cesta: boolean
  oferta_id: string | null
  hora_atividade_id: string | null
  turma_id: string | null
  turma: string | null
  turma_cor: string | null
  serie: number | null
  disciplina_id: string | null
  disciplina: string | null
  disciplina_abrev: string | null
  disciplina_cor: string | null
  local_id: string | null
  local: string | null
  professores_ids: string[]
  professores: string[]
}

export interface PendenciaGrade {
  oferta_id: string
  aulas_nao_alocadas: number
  motivo: string
  turma: string
  disciplina: string
  /** Rótulos das regras que bloquearam esta oferta, quando o núcleo a nomeia. */
  restricoes_ids: string[]
}

/**
 * O que o motor conseguiu PROVAR sobre as aulas que sobraram.
 *
 * `conclusao` é a distinção que a tela existe para carregar: `RESTRICAO`
 * significa que rodar de novo não adianta, e `TEMPO` significa exatamente o
 * contrário. Tratar as duas como "deu ruim" faria a tela mandar o coordenador
 * mexer numa regra sadia.
 */
export interface Diagnostico {
  status_solucao: string | null
  terminado_em: string | null
  conclusao: 'NAO_EXECUTADO' | 'RESTRICAO' | 'TEMPO' | 'INDETERMINADO'
  nucleo: { restricoes_ids: string[]; entidades_envolvidas: string[] } | null
  relaxacoes: RelaxacaoGrade[]
}

export interface RelaxacaoGrade {
  id: string
  tipo: string
  alvo_id: string
  alvo_descricao: string
  valor_atual: string
  valor_proposto: string
  custo_estimado: number
  /** Provado que afrouxar só esta bastaria. Falso = envolvida, mas não basta. */
  resolve_sozinha: boolean
  restricoes_resolvidas: string[]
}

export interface ViolacaoGrade {
  id: string
  criterio_id: string
  /** De −10 (péssimo) a +5. Zero é indiferente. Ver `Grau` no contrato. */
  importancia: number
  mensagem: string
  relaxacao_id: string | null
  alocacoes_ids: string[]
}

export interface Horario {
  cenario: { id: string; nome: string }
  slots: SlotGrade[]
  alocacoes: AlocacaoGrade[]
  pendencias: PendenciaGrade[]
  violacoes: ViolacaoGrade[]
  diagnostico: Diagnostico
}

/**
 * O custo mensal da grade. Ver `custo.ts` na API.
 *
 * **Tudo em centavos inteiros**, e a conversão para reais acontece só na
 * borda que mostra. Dinheiro somado trezentas vezes em ponto flutuante não
 * fecha com a folha, e a primeira coisa que fazem com este número é
 * conferi-lo contra ela.
 */
export interface LinhaDeCusto {
  professor_id: string
  professor: string
  classificacao: string | null
  regime: string | null
  valor_hora_aula: number | null
  aulas_semana: number
  janela_semana: number
  aula_mes: number
  janela_mes: number
  total_mes: number
}

export interface Custo {
  linhas: LinhaDeCusto[]
  aula_mes: number
  janela_mes: number
  total_mes: number
  sem_valor: { id: string; nome: string; aulas_semana: number }[]
  por_classificacao: {
    classificacao: string
    professores: number
    aulas_semana: number
    total_mes: number
  }[]
  semanas_no_mes: number
  /** Sem grade, "janela: R$ 0" não quer dizer grade boa: quer dizer que ninguém sabe. */
  grade_montada: boolean
  /** De onde vem o detalhe: da grade montada, ou do planejado quando não há grade. */
  base: 'GRADE' | 'ATRIBUICAO'
  /** O que custa quem a coordenação decidiu que dá cada aula. Existe sempre. */
  atribuicao: ResumoDeCusto
  /** O que custa quem está de fato em cada aula deste cenário. Só com grade. */
  grade: (ResumoDeCusto & { aulas_alocadas: number; aulas_pendentes: number }) | null
  /** Grade menos planejado, em centavos. Negativo pode ser economia ou pendência. */
  diferenca_mes: number | null
}

export interface ResumoDeCusto {
  aula_mes: number
  janela_mes: number
  total_mes: number
  professores: number
}

/** Um achado da análise que roda ANTES do motor. Ver `analise.ts` na API. */
export interface Achado {
  gravidade: 'IMPEDE' | 'APERTA'
  tipo: string
  mensagem: string
  alvo_id?: string
}

/** A escolha única que precede os 32 pesos. Ver `enfases.py` no contrato. */
export interface Enfase {
  id: 'EQUILIBRADO' | 'JANELAS' | 'GEMINAR' | 'JANELAS_E_GEMINAR' | 'PERSONALIZADO'
  nome: string
  descricao: string
}

export interface Cenario {
  id: string
  nome: string
  descricao: string
  alocacoes: number
  pendencias: number
  enfase: Enfase['id']
  /** De qual cenário esta rodada partiu. Nulo em cenário comum. */
  base_id: string | null
  /** O filtro que decidiu o que ficou congelado. */
  congelamento: Congelamento | null
}

/** O que fica congelado numa rodada. Ver `rodada.ts` na API. */
export interface Congelamento {
  tudo?: boolean
  turmas?: string[]
  professores?: string[]
  disciplinas?: string[]
  dias?: number[]
  exceto_turmas?: string[]
  exceto_professores?: string[]
  exceto_disciplinas?: string[]
}

/** O que um cenário mudou em relação à base dele. Ver `diferencas.ts` na API. */
export type DiferencaDaBase =
  | { estado: 'PROCESSANDO' }
  | { estado: 'SEM_SAIDA' }
  | {
      estado: 'OK'
      encaixadas: number
      desencaixadas: number
      mudancas: {
        oferta_id: string
        disciplina: string
        turma: string
        saiu: { dia: number; inicio: number }[]
        entrou: { dia: number; inicio: number }[]
        professores_antes: string[]
        professores_depois: string[]
      }[]
    }

export const sugestao = {
  pedir: (projeto: string, cenario: string, pendencia: string) =>
    pedir<{ cenario: { id: string; nome: string; descricao: string }; faltando: string[] }>(
      `/projetos/${projeto}/cenarios/${cenario}/pendencias/${pendencia}/sugestao`,
      { method: 'POST', body: '{}' },
    ),

  diferencas: (projeto: string, cenario: string) =>
    pedir<DiferencaDaBase>(`/projetos/${projeto}/cenarios/${cenario}/diferencas`),
}

/** Para onde uma aula pode ir, com os avisos de cada horário. Ver `movimento.ts`. */
export interface OpcoesDeMovimento {
  aula: { id: string; disciplina: string; turma: string; fixada: boolean; na_cesta: boolean }
  horarios: {
    slot_id: string
    dia: number
    inicio: number
    duracao_min: number
    atual: boolean
    avisos: { tipo: string; duro: boolean; mensagem: string }[]
  }[]
}

export const movimento = {
  opcoes: (projeto: string, cenario: string, alocacao: string) =>
    pedir<OpcoesDeMovimento>(`/projetos/${projeto}/cenarios/${cenario}/alocacoes/${alocacao}/opcoes`),

  mover: (
    projeto: string,
    cenario: string,
    alocacao: string,
    mudanca: { slot_id?: string; fixada?: boolean; confirmar?: boolean; motivo?: string },
  ) =>
    pedir<{ id: string; fixada: boolean; na_cesta: boolean }>(
      `/projetos/${projeto}/cenarios/${cenario}/alocacoes/${alocacao}`,
      { method: 'PATCH', body: JSON.stringify(mudanca) },
    ),
}

/** Uma pendência acompanhada. Ver `rotas/pendencias.ts` na API. */
export type EstadoPendencia = 'ABERTA' | 'EM_NEGOCIACAO' | 'AGUARDANDO' | 'RESOLVIDA' | 'DESCARTADA'

export interface Pendencia {
  id: string
  chave: string
  tipo: string
  mensagem: string
  estado: EstadoPendencia
  oferta_id: string | null
  turma_id: string | null
  responsavel_id: string | null
  responsavel: string | null
  visto_em: string
  sumiu_em: string | null
  criado_em: string
  notas: number
  ultima_nota: string | null
}

export interface NotaDePendencia {
  id: string
  texto: string
  criado_em: string
  autor_id: string | null
  autor: string | null
}

export interface TabelaDeElegiveis {
  horarios: { slot_id: string; dia: number; inicio: number; duracao_min: number }[]
  professores: {
    id: string
    nome: string
    codigo_externo: string | null
    ja_na_oferta: boolean
    livres: number
    celulas: ('LIVRE' | 'NAO_LIBEROU' | 'OCUPADO')[]
  }[]
  motivo?: string
}

export const pendencias = {
  quadro: (projeto: string, cenario: string) =>
    pedir<{ itens: Pendencia[]; contagem: Partial<Record<EstadoPendencia, number>> }>(
      `/projetos/${projeto}/cenarios/${cenario}/pendencias`,
    ),

  alterar: (projeto: string, id: string, mudanca: { estado?: EstadoPendencia; responsavel_id?: string | null }) =>
    pedir<Pendencia>(`/projetos/${projeto}/pendencias/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(mudanca),
    }),

  notas: (projeto: string, id: string) =>
    pedir<{ itens: NotaDePendencia[] }>(`/projetos/${projeto}/pendencias/${id}/notas`).then((r) => r.itens),

  anotar: (projeto: string, id: string, texto: string) =>
    pedir<NotaDePendencia>(`/projetos/${projeto}/pendencias/${id}/notas`, {
      method: 'POST',
      body: JSON.stringify({ texto }),
    }),

  elegiveis: (projeto: string, cenario: string, id: string) =>
    pedir<TabelaDeElegiveis>(`/projetos/${projeto}/cenarios/${cenario}/pendencias/${id}/elegiveis`),
}

/** Quais regras valeram na última grade. Ver `regras.ts` na API. */
export interface RegrasDaGrade {
  estado: 'OK' | 'NAO_PROCESSADO' | 'SEM_INVENTARIO'
  processado_em?: string
  total: number
  grupos: {
    tipo: string
    titulo: string
    explicacao: string
    quantidade: number
    deduzidas: number
    regras: {
      id: string
      descricao: string
      valor_atual: string
      origem: 'DECLARADA' | 'DEDUZIDA' | 'DECLARADA_E_DEDUZIDA'
    }[]
  }[]
}

export interface PreviaDaRodada {
  total: number
  congeladas: number
  ja_fixadas: number
  descricao: string
}

export interface Eixos {
  turmas: { id: string; nome: string; serie: number; cor: string | null }[]
  professores: { id: string; nome: string; abreviatura: string; codigo_externo: string | null }[]
  locais: { id: string; nome: string }[]
}

export const horario = {
  cenarios: (projeto: string) =>
    pedir<{ itens: Cenario[] }>(`/projetos/${projeto}/cenarios`).then((r) => r.itens),

  eixos: (projeto: string) => pedir<Eixos>(`/projetos/${projeto}/horario/eixos`),

  ler: (projeto: string, cenario: string) =>
    pedir<Horario>(`/projetos/${projeto}/cenarios/${cenario}/horario`),

  enfases: (projeto: string) =>
    pedir<{ itens: Enfase[] }>(`/projetos/${projeto}/enfases`).then((r) => r.itens),

  definirEnfase: (projeto: string, cenario: string, enfase: Enfase['id']) =>
    pedir<Cenario>(`/projetos/${projeto}/cenarios/${cenario}`, {
      method: 'PATCH',
      body: JSON.stringify({ enfase }),
    }),

  comparar: (projeto: string, ids: string[]) =>
    pedir<{ colunas: any[]; linhas: any[] }>(
      `/projetos/${projeto}/cenarios/comparar?ids=${ids.join(',')}`,
    ),

  analise: (projeto: string, cenario: string) =>
    pedir<{ faltando: string[]; achados: Achado[] }>(
      `/projetos/${projeto}/cenarios/${cenario}/analise`,
    ),

  custo: (projeto: string, cenario: string) =>
    pedir<Custo>(`/projetos/${projeto}/cenarios/${cenario}/custo`),

  processar: (projeto: string, cenario: string) =>
    pedir<{ id: string; status: string }>(`/projetos/${projeto}/cenarios/${cenario}/processar`, {
      method: 'POST',
    }),

  regras: (projeto: string, cenario: string) =>
    pedir<RegrasDaGrade>(`/projetos/${projeto}/cenarios/${cenario}/regras`),

  previaRodada: (projeto: string, base: string, congelamento: Congelamento) =>
    pedir<PreviaDaRodada>(`/projetos/${projeto}/cenarios/${base}/rodadas/previa`, {
      method: 'POST',
      body: JSON.stringify({ congelamento }),
    }),

  criterios: (projeto: string, cenario: string) =>
    pedir<{ itens: { criterio_id: string; custo: number; ocorrencias: number }[] }>(
      `/projetos/${projeto}/cenarios/${cenario}/criterios`,
    ).then((r) => r.itens),

  rodar: (projeto: string, base: string, congelamento: Congelamento, nome?: string, objetivo?: string) =>
    pedir<{
      cenario: { id: string; nome: string; descricao: string }
      congeladas: number
      total: number
      processamento: { id: string } | null
      faltando: string[]
    }>(`/projetos/${projeto}/cenarios/${base}/rodadas`, {
      method: 'POST',
      body: JSON.stringify({ congelamento, ...(nome ? { nome } : {}), ...(objetivo ? { objetivo } : {}) }),
    }),

  processamentos: (projeto: string) =>
    pedir<{ itens: any[] }>(`/projetos/${projeto}/processamentos`).then((r) => r.itens),
}

// ---------------------------------------------------------------------------
// Importação de planilha
// ---------------------------------------------------------------------------

export interface TempoImportado {
  rotulo: string
  inicio: number
  fim: number
}

export interface AvisoImportacao {
  gravidade: 'IMPEDE' | 'REVISAR'
  tipo: string
  mensagem: string
  linha?: number
}

export interface RespostaImportada {
  linha: number
  nome: string
  matricula: string | null
  email: string | null
  marcacoes: number
  observacao: string | null
  /** Quando foi enviada, se a planilha diz. */
  respondida_em: string | null
  /** Linhas das respostas anteriores da mesma pessoa, que ficaram de fora. */
  anteriores: number[]
}

export interface Casada extends RespostaImportada {
  professor_id: string
  professor_nome: string
  como: 'MATRICULA' | 'NOME' | 'NOME_PARECIDO'
}

export interface Ambigua extends RespostaImportada {
  candidatos: { id: string; nome: string; codigo_externo: string | null }[]
}

export interface Previa {
  /** `false` na conferência; o que foi gravado, depois de aplicar. */
  aplicado: false | { professores: number; janelas: number; ignoradas_sem_marcacao: number }
  tempos: TempoImportado[]
  dias: number[]
  resumo: {
    respostas: number
    casadas: number
    conferir: number
    sem_cadastro: number
    ambiguas: number
    disputados: number
    sem_resposta: number
    janelas: number
  }
  avisos: AvisoImportacao[]
  casadas: Casada[]
  conferir: Casada[]
  sem_cadastro: RespostaImportada[]
  ambiguas: Ambigua[]
  disputados: { professor_id: string; professor_nome: string; respostas: RespostaImportada[] }[]
  sem_resposta: { id: string; nome: string; codigo_externo: string | null }[]
}

/**
 * Sobe a planilha.
 *
 * Não passa por `pedir` porque `multipart/form-data` carrega uma fronteira
 * gerada pelo navegador — declarar o `content-type` à mão quebraria o corpo
 * sem dizer por quê.
 */
export async function subirDisponibilidade(
  projeto: string,
  arquivo: File,
  aplicar = false,
): Promise<Previa> {
  const corpo = new FormData()
  corpo.set('arquivo', arquivo)
  if (aplicar) corpo.set('aplicar', 'true')

  const resposta = await fetch(`${BASE}/projetos/${projeto}/importacoes/disponibilidade`, {
    method: 'POST',
    credentials: 'include',
    body: corpo,
  })
  if (!resposta.ok) {
    const erro = await resposta.json().catch(() => ({}))
    throw new ErroApi(resposta.status, (erro as { erro?: string }).erro ?? resposta.statusText)
  }
  return (await resposta.json()) as Previa
}

// ---------------------------------------------------------------------------
// Atribuição de turmas
// ---------------------------------------------------------------------------

export interface DisciplinaAtribuivel {
  id: string
  nome: string
  turmas: number
  candidatos: number
  atribuicao_id: string | null
  atribuicao_nome: string | null
}

export interface OfertaDaDisciplina {
  id: string
  turma_id: string
  turma_nome: string
  serie: number
  aulas_semanais: number
  professores_fixos: { id: string; nome: string }[]
  atribuicao_id: string | null
}

export interface Cota {
  professor_id: string
  nome?: string
  minimo: number
  maximo: number | null
}

export interface EstadoDaAtribuicao {
  disciplina: { id: string; nome: string }
  ofertas: OfertaDaDisciplina[]
  cotas: Cota[]
  professores: { id: string; nome: string; codigo_externo: string | null; max_aulas_semanais: number | null }[]
  /** Quantos professores cada turma do grupo leva. 2 = a dupla. */
  professores_por_oferta: number
}

export const atribuicao = {
  disciplinas: (projeto: string) =>
    pedir<{ itens: DisciplinaAtribuivel[] }>(
      `/projetos/${projeto}/atribuicao/disciplinas`,
    ).then((r) => r.itens),

  ler: (projeto: string, disciplina: string) =>
    pedir<EstadoDaAtribuicao>(`/projetos/${projeto}/atribuicao/disciplinas/${disciplina}`),

  gravar: (
    projeto: string,
    disciplina: string,
    corpo: { nome?: string; ofertas_ids: string[]; cotas: Cota[]; professores_por_oferta?: number },
  ) =>
    pedir<{ atribuicao_id: string | null }>(
      `/projetos/${projeto}/atribuicao/disciplinas/${disciplina}`,
      { method: 'PUT', body: JSON.stringify(corpo) },
    ),
}

// ---------------------------------------------------------------------------
// O formulário de disponibilidade dirigido (P12)
// ---------------------------------------------------------------------------

export type EstadoDoFormulario = 'SEM_LINK' | 'ENVIADO' | 'RESPONDIDO' | 'VENCIDO'

export interface FormularioDoProfessor {
  id: string
  nome: string
  codigo_externo: string | null
  email: string | null
  estado: EstadoDoFormulario
  enviado_em: string | null
  expira_em: string | null
  respondido_em: string | null
  justificativa: string | null
  convite_enviado_em: string | null
  com_carga: boolean
  /** O pedido em frases, como o professor o lê. */
  pedido: string[]
  /** O que falta com a disponibilidade de hoje, respondida ou importada. */
  faltas: string[]
}

export interface LinkDeFormulario {
  professor_id: string
  nome: string
  email: string | null
  token: string
  expira_em: string
}

export const formularios = {
  listar: (projeto: string) =>
    pedir<{
      tempos: number
      professores: FormularioDoProfessor[]
      convite_padrao: string
      /** teste: tudo vai para `testes`; bloqueado: nada sai; real: vai para os professores. */
      email: { modo: 'teste' | 'real' | 'bloqueado'; testes: string[]; smtp_configurado: boolean }
    }>(`/projetos/${projeto}/formularios`),

  /** Gera os links e manda o convite por e-mail — sujeito à trava do modo de teste. */
  enviar: (projeto: string, professores_ids: string[]) =>
    pedir<{
      links: LinkDeFormulario[]
      modo: 'teste' | 'real' | 'bloqueado'
      para_teste: string[]
      enviados: number
      sem_email: string[]
      falhas: { professor_id: string; erro: string }[]
    }>(`/projetos/${projeto}/formularios/enviar`, { method: 'POST', body: JSON.stringify({ professores_ids }) }),

  gerar: (projeto: string, professores_ids: string[], dias?: number) =>
    pedir<{ links: LinkDeFormulario[] }>(`/projetos/${projeto}/formularios`, {
      method: 'POST',
      body: JSON.stringify({ professores_ids, dias }),
    }).then((r) => r.links),

  revogar: (projeto: string, professor: string) =>
    pedir<{ revogados: number }>(`/projetos/${projeto}/formularios/${professor}`, { method: 'DELETE' }),

  /** Estende o prazo do mesmo link — o professor usa o que já recebeu. */
  reabrir: (projeto: string, professores_ids: string[], dias?: number) =>
    pedir<{ reabertos: number; expira_em: string | null; sem_link: string[] }>(
      `/projetos/${projeto}/formularios/reabrir`,
      { method: 'POST', body: JSON.stringify({ professores_ids, dias }) },
    ),
}

/** O que o professor vê. Nenhum campo fala de disciplina ou turma — de propósito. */
export interface FormularioPublico {
  professor: string
  escola: string
  periodo: string | null
  expira_em: string
  respondido_em: string | null
  justificativa: string | null
  turnos: { id: string; nome: string }[]
  tempos: { dia: number; inicio: number; fim: number; turno_id: string }[]
  marcados: string[]
  pedido: { exigencias: { turno_id: string | null; dia: number | null; aulas: number }[]; total: number } | null
  frases: string[]
  /** 2 na ESPM: cada quadradinho é um tempo de 1h40, e o pedido conta tempos. */
  creditos_por_tempo: number
}

export const formularioPublico = {
  ler: (token: string) => pedir<FormularioPublico>(`/formulario/${encodeURIComponent(token)}`),

  /** 422 traz as faltas em frases; por isso não passa por `pedir`. */
  async enviar(token: string, marcados: string[], justificativa?: string) {
    const r = await fetch(`${BASE}/formulario/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ marcados, justificativa }),
    })
    const corpo = (await r.json().catch(() => ({}))) as { erro?: string; faltas?: string[] }
    if (r.ok) return { gravado: true as const, faltas: corpo.faltas ?? [] }
    if (r.status === 422 && corpo.faltas) return { gravado: false as const, faltas: corpo.faltas }
    throw new ErroApi(r.status, corpo.erro ?? r.statusText)
  },
}
